import { Breakpoints } from '~/lib/useResponsive';
import {
  HORIZONTAL_AUDIO_PLAYER,
  CARD,
  CardDefinition,
  IMAGE,
  ImageDefinition,
  ItemIds,
  LayoutListDefinition,
  LAYOUT_LIST,
  TEXT,
  Alignment,
  VideoDefinition,
  VIDEO
} from '../../items';
import { HorizontalAudioPlayerDefinition } from '../../items/audio-player/HorizontalAudioPlayer';
import { ItemDefinition } from '../../items/Item';
import { ItemHelper, ItemOfType } from './types';
import { isIOS, isSafari } from 'react-device-detect';

/**
 * Turns an API Card item into a tree of items with a CARD item as root
 * @param src The API Card item
 * @returns A CARD item with child items
 */
export const CardTinderHelper: ItemHelper<
  'Card' | 'Question',
  CardDefinition,
  {
    width?: CardDefinition['values']['width'];
    height?: CardDefinition['values']['height'];
    padding?: CardDefinition['values']['padding'];
    extraItems?: ItemDefinition<ItemIds>[];
    align?: Alignment;
    clearStyles?: Breakpoints;
    canScroll?: boolean;
    items?: ItemOfType<'Question'>['items'];
    linkedAudioId?: string;
    className?: string;
    /** Padding of the inline (card) audio player, e.g. '0px'. Defaults to '16px'. */
    audioPadding?: string;
  }
> = (src, options) => {
  let rootItem: LayoutListDefinition | null = null;
  let videos: VideoDefinition[] = [];

  if (src.__typename === 'Question' && src.videos?.length) {
    videos = src.videos.map(v => {
      const mediaUrl =
        isIOS || isSafari
          ? ([
              v.mediaUrl && { src: v.mediaUrl, type: 'video/mp4' }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ].filter(Boolean) as any)
          : ([
              v.hlsUrl && {
                src: v.hlsUrl,
                type: 'application/vnd.apple.mpegurl'
              },
              v.mediaUrl && { src: v.mediaUrl, type: 'video/mp4' }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ].filter(Boolean) as any);

      return {
        type: VIDEO,
        id: v.id,
        values: {
          videoId: v?.id,
          mediaUrl: mediaUrl,
          autoPlay: false,
          subtitlesUrl: v.subtitlesUrl,
          snapshotUrl: v.snapshotUrl,
          showPlayButton: true,
          isolated: true,
          loop: v.loop || false,
          replay: true,
          videoContainerStyles: {
            width: '100%',
            height: '233px',
            position: 'relative'
          }
        }
      } as VideoDefinition;
    });
  }

  let pictures: ImageDefinition[] = [];
  if (src.pictures?.length) {
    pictures = src.pictures.map(
      p =>
        ({
          id: p.id,
          type: IMAGE,
          values: {
            imageGenerationPlacement: 'card_image',
            height: 280,
            url: p.mediaUrl,
            alt: p.name
          },
          items: []
        }) as ImageDefinition
    );
  }

  if (pictures.length || videos.length) {
    rootItem = {
      id: `${src.id}.list`,
      type: LAYOUT_LIST,
      values: {},
      items: [...pictures, ...videos]
    };
  }

  const textList: LayoutListDefinition = {
    id: `${src.id}.text-list`,
    type: LAYOUT_LIST,
    values: {
      padded: true,
      align: (options && options.align) || 'center'
    },
    items: []
  };

  if (src.subtitle) {
    textList.items.push({
      id: `${src.id}.subtitle`,
      type: TEXT,
      values: {
        value: src.subtitle,
        variant: 'sub_heading',
        icon: src?.icon
      },
      items: []
    });
  }

  if (src.title) {
    textList.items.push({
      id: `${src.id}.title`,
      type: TEXT,
      values: {
        value: src.title,
        variant: 'heading3'
      },
      items: []
    });
  }

  if (src.body) {
    textList.items.push({
      id: `${src.id}.body`,
      type: TEXT,
      values: {
        value: src.body,
        canScroll: options && options.canScroll
      },
      items: []
    });
  }

  if (src.audios && src.audios.length) {
    const i = 0;

    textList.items.push({
      type: HORIZONTAL_AUDIO_PLAYER,
      id: src.audios[i].id,
      items: [],
      values: {
        mediaUrl: src.audios[i].mediaUrl,
        subtitlesUrl: src.audios[i].subtitlesUrl,
        autoPlay: src.audios[i].autoPlay,
        name: src.audios[i].name,
        blocking: src.__typename === 'Card',
        replay: false,
        parentId: src.id,
        generated: src.audios?.[i]?.generated || false,
        variant: 'card',
        fullBleed: true,
        ...(options?.audioPadding ? { padding: options.audioPadding } : {})
      }
    } as HorizontalAudioPlayerDefinition);
  }

  if (options?.extraItems?.length) {
    textList.items = [...textList.items, ...options.extraItems];
  }

  if (rootItem && textList.items.length) {
    rootItem.items.push(textList);
  }

  return {
    id: src.id,
    position: src.position || undefined,
    start: src.appear || undefined,
    type: CARD,
    values: options || {},
    items: [rootItem || textList]
  };
};
